import {
  pathOr,
  pipe,
  contains,
  append,
  reduce,
  prop,
  sortWith,
  descend,
  filter,
  equals,
  includes,
} from 'ramda';
import {
  DOCUMENT_PHASE_SCRAPER_FAILED,
  DOCUMENT_PHASE_VALIDATION_FAILED,
  DOCUMENT_PHASE_AUTHENTICITY_FAILED,
  DOCUMENT_PHASE_SCRAPPED,
} from 'src/components/CheckLists/documents';

import {
  STATUS_PENDING,
  OBJECT_META,
  LABELS,
  SUCCESS_UPPERCASE_S,
} from 'src/services/constants';

const Errors = [
  DOCUMENT_PHASE_AUTHENTICITY_FAILED,
  DOCUMENT_PHASE_VALIDATION_FAILED,
  DOCUMENT_PHASE_SCRAPER_FAILED,
];

const hasDocumentPhases = (document: any, phases: string[] = []) => {
  return phases.indexOf(pathOr('', ['status', 'phase'], document)) > -1;
};

export const hasDocumentError = (document: any) => {
  return hasDocumentPhases(document, Errors);
};

export const hasDocumentPending = (document: any) => {
  return hasDocumentPhases(document, [STATUS_PENDING]);
};

export const hasDocumentIsStillNotProcessed = (document: any) => {
  return hasDocumentPhases(document, [STATUS_PENDING, DOCUMENT_PHASE_SCRAPPED]);
};
export const filterDocumentPhase = (documents: any, phase: string) => {
  const isEqualPhase = (n: any) => equals(n.status.phase, phase);
  return filter(isEqualPhase, documents);
};

export const filterDocument = (documents: any, category: string) => {
  const isEqualCategory = (n: any) =>
    equals(pathOr('', [OBJECT_META, LABELS, 'category'], n), category);
  return filter(isEqualCategory, documents);
};
export const filterDocumentWithCategorys = (documents: any, category: any) => {
  const isEqualCategory = (n: any) =>
    includes(pathOr('', [OBJECT_META, LABELS, 'category'], n), category);
  return filter(isEqualCategory, documents);
};
export const omitDocumentCategory = (documents: any, category: string) => {
  const isEqualCategory = (n: any) =>
    !contains(pathOr('', [OBJECT_META, LABELS, 'category'], n), category);
  return filter(isEqualCategory, documents);
};

export const isEqualCategoryAndStatus = (n: any, category: string) =>
  equals(pathOr('', [OBJECT_META, LABELS, 'category'], n), category) &&
  equals(pathOr('', [OBJECT_META, LABELS, 'upload'], n), SUCCESS_UPPERCASE_S);

export const filterDocumentWithCategoryAndStatus = (
  documents: any,
  category: string
) => {
  return filter((n) => isEqualCategoryAndStatus(n, category), documents);
};

export const filterDocumentsUploadedSuccess = (
  documents: any,
  category: string
) => {
  const isEqualCategoryAndStatus = (n: any) =>
    equals(pathOr('', [OBJECT_META, LABELS, 'upload'], n), SUCCESS_UPPERCASE_S);
  return filter(isEqualCategoryAndStatus, documents);
};

export const filterCategoriedDocuments = (
  documents: any,
  supporedCategories: string[]
) => {
  const categorizedDocuments: any = [];
  supporedCategories.forEach((category: string) => {
    categorizedDocuments.push(...filterDocument(documents, category));
  });
  return categorizedDocuments;
};

export const filterCategoriedFailedDocuments = (
  documents: any,
  supporedCategories: string[]
) => {
  const categorizedDocuments = filterCategoriedDocuments(
    documents,
    supporedCategories
  );
  const errorDocuments: any = [];
  Errors.forEach((phase: string) => {
    errorDocuments.push(...filterDocumentPhase(categorizedDocuments, phase));
  });
  return errorDocuments;
};

export const filterCategoriedPendingDocuments = (
  documents: any,
  supporedCategories: string[],
  phase: any
) => {
  return filterDocumentPhase(
    filterCategoriedDocuments(documents, supporedCategories),
    phase
  );
};

const getCategory = (x: any) =>
  pathOr('', [OBJECT_META, LABELS, 'category'], x);

/* *
 * getTheLatestInEveryCategory provides the latest set of documents per category.
 * For instance the ID might have uploaded twice, with one failure. We need the lasted in processing ID document.
 *
 * @params { documents } array of documents
 */

export const getTheLatestInEveryCategory = (documents: any) => {
  return pipe(
    sortWith([descend(prop('created_at'))]),
    reduce(
      ({ results, foundOnce }, item) =>
        contains(getCategory(item), foundOnce.map(getCategory))
          ? { results: append(item, results), foundOnce }
          : { results, foundOnce: append(item, foundOnce) },
      { results: [], foundOnce: [] }
    ),
    prop('foundOnce')
  )(documents);
};

export const getDescendingArrangementForDocumentPointer = (
  documentPointers: any
) => {
  return sortWith([descend(prop('created_at'))])(documentPointers);
};
